import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SignIn, SignUp, SignedIn, SignedOut, useAuth, useClerk } from '@clerk/clerk-react';
import './App.css';
import './pages/LoginPage.css';
import Sidebar, { CLIENT_PAGES } from './components/Sidebar';
import IdleTimeout from './components/IdleTimeout';
import EligibilityPage from './pages/EligibilityPage';
import ClaimsLedgerPage from './pages/ClaimsLedgerPage';
import PracticesPage from './pages/PracticesPage';
import PracticeDetailsPage from './pages/PracticeDetailsPage';
import PaymentsPage from './pages/PaymentsPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ARPage from './pages/ARPage';
import { WaterfallPage } from './features/waterfall';
import SuccessPage from './pages/SuccessPage';
import MissionControlPage from './pages/MissionControlPage';
import ExecReviewPage from './pages/ExecReviewPage';
import DataReadinessPage from './pages/DataReadinessPage';
import RulesPage from './pages/RulesPage';
import OnboardingPage from './pages/OnboardingPage';
import TasksPage from './pages/TasksPage';
import MailPage from './pages/MailPage';
import FeatureOff from './components/FeatureOff';
import { FEATURE_PAGE_IDS, findFeaturePage } from './features/registry';
import { setClaimEditorTarget } from './features/claims';
import { ClaimListPage } from './features/claimLists';
import useCapabilities from './hooks/useCapabilities';

const API_URL = process.env.REACT_APP_API_URL || '';

// Where an invitation link lands (the backend's INVITE_SIGN_UP_PATH). Clerk's email
// carries __clerk_ticket for a user who does not exist yet, and only <SignUp>
// (strategy "ticket") can consume it; <SignIn> resolves existing identifiers only and
// shows "This invitation refers to a non-existing identification" (2026-10-22, David's
// invites from next, after #481 made signInUrl absolute on our host so the link no
// longer went through Clerk's hosted pages).
export const SIGN_UP_PATH = '/sign-up';

export function isSignUpRoute(pathname = window.location.pathname) {
  return pathname.replace(/\/+$/, '') === SIGN_UP_PATH;
}

// An invitation (or any Clerk ticket flow) in the URL: __clerk_ticket, or the
// __clerk_status Clerk appends on its way back. Old links that still point at "/"
// are sent to the sign-up route with the query string intact.
export function carriesClerkTicket(search = window.location.search) {
  const params = new URLSearchParams(search);
  return params.has('__clerk_ticket') || params.has('__clerk_status');
}

// Where <SignIn> sends the user once signed in: the path they opened, so a deep link
// (a Slack bookmark to /p/3/tasks, say) survives signing in instead of landing on "/"
// and losing the practice and page; the restore-from-URL effect below then reads it.
// Pathname only: Clerk appends its own query on the way back, and the sign-up route
// is the invitation flow's, not a page to come back to.
export function postSignInPath(pathname, signUpPath = SIGN_UP_PATH) {
  const path = (pathname || '').trim();
  if (!path || path === '/' || path.replace(/\/+$/, '') === signUpPath) return '/';
  return path;
}

// --- URL <-> navigation-state sync (Option A: native History API, no router) ---
// Atlas has no router; navigation is two state vars (selectedPractice + activePage).
// We mirror those to the URL path so the browser Back/Forward buttons and deep
// links work. Backend already serves index.html for unknown paths (Flask 404
// handler), so a hard refresh on these paths boots the app correctly.
const VALID_PAGES = [
  'dashboard', 'eligibility', 'claims', 'payments',
  'ar', 'waterfall', 'practice-details', 'appointments', 'admin', 'mission-control', 'rules', 'exec-review',
  // The Customer Success page (docs/slack-pane.md): the practice's weekly-report numbers,
  // the Slack tab's bookmark. Staff only; a client deep link lands on the dashboard.
  'success',
  // Cross-practice data readiness grid; super_admin, like Mission Control.
  'data-readiness',
  // Practice onboarding (docs/onboarding.md): cross-practice, admin, behind user.onboarding_enabled.
  'onboarding',
  // The practice's client tasks (docs/slack-pane.md): staff only, not on CLIENT_PAGES.
  'tasks',
  // The practice's mail threads (docs/slack-pane.md): staff only, not on CLIENT_PAGES.
  'mail',
  // Gated pages from the feature registry. They are deep-linkable like every other
  // page; whether one renders is the gate's call, not the URL's.
  ...FEATURE_PAGE_IDS,
];

function stateToPath(selectedPractice, activePage, listId = null) {
  // A claim list (features/claimLists): /p/<id>/lists/<listId>, the link a biller is sent.
  if (selectedPractice && activePage === 'lists' && listId) {
    return `/p/${selectedPractice.id}/lists/${listId}`;
  }
  if (selectedPractice) return `/p/${selectedPractice.id}/${activePage}`;
  if (activePage === 'admin') return '/admin';
  // Spans every practice, so it has a path of its own rather than one under /p/<id>.
  if (activePage === 'mission-control') return '/mission-control';
  if (activePage === 'rules') return '/rules';
  // The Monday ledger review across every practice; super_admin, like Mission Control.
  if (activePage === 'exec-review') return '/exec-review';
  // Which data streams reach Atlas for every practice; super_admin, like Mission Control.
  if (activePage === 'data-readiness') return '/data-readiness';
  // Practices needing linking: cross-practice like Mission Control.
  if (activePage === 'onboarding') return '/onboarding';
  return '/practices';
}

function parsePath(pathname) {
  const parts = (pathname || '').split('/').filter(Boolean);
  if (parts[0] === 'p' && parts[1]) {
    const id = parseInt(parts[1], 10);
    const practiceId = Number.isNaN(id) ? null : id;
    // /p/<id>/claims/<claimId> — the claim editor's deep link. The editor now opens as
    // a drawer over the ledger rather than as its own page, so this lands on the ledger
    // and names the claim; the ledger opens the drawer (David's walk, 2026-09-15).
    // Bookmarks made before that still work.
    if (parts[2] === 'claims' && parts[3]) {
      const claimId = parseInt(parts[3], 10);
      return {
        practiceId,
        page: 'claims',
        claimId: Number.isNaN(claimId) ? null : claimId,
      };
    }
    // /p/<id>/lists/<listId> — a claim list (features/claimLists). Without an id there is
    // no list to show, so it lands on the Claims page, where the Lists strip is.
    if (parts[2] === 'lists') {
      const listId = parts[3] ? parseInt(parts[3], 10) : NaN;
      return Number.isNaN(listId)
        ? { practiceId, page: 'claims' }
        : { practiceId, page: 'lists', listId };
    }
    const page = VALID_PAGES.includes(parts[2]) ? parts[2] : 'dashboard';
    return { practiceId, page };
  }
  if (parts[0] === 'admin') return { practiceId: null, page: 'admin' };
  if (parts[0] === 'mission-control') return { practiceId: null, page: 'mission-control' };
  if (parts[0] === 'rules') return { practiceId: null, page: 'rules' };
  if (parts[0] === 'exec-review') return { practiceId: null, page: 'exec-review' };
  if (parts[0] === 'data-readiness') return { practiceId: null, page: 'data-readiness' };
  if (parts[0] === 'onboarding') return { practiceId: null, page: 'onboarding' };
  return { practiceId: null, page: null };
}

// "View as client" (super_admin only). sessionStorage so a demo survives a reload and
// ends with the tab; every access is wrapped because it throws in a private window
// with site data blocked (same reasoning as Sidebar's readCollapsed).
const VIEW_AS_CLIENT_KEY = 'atlas.viewAsClient';

function readViewAsClient() {
  try {
    return window.sessionStorage.getItem(VIEW_AS_CLIENT_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function writeViewAsClient(on) {
  try {
    if (on) window.sessionStorage.setItem(VIEW_AS_CLIENT_KEY, '1');
    else window.sessionStorage.removeItem(VIEW_AS_CLIENT_KEY);
  } catch (err) {
    /* a private window with site data blocked: the switch still works, it just forgets */
  }
}

function App({ idleTimeoutMinutes = 15, idleWarningSeconds = 60 }) {
  const [activePage, setActivePage] = useState('eligibility');
  // The open claim list's id while activePage is 'lists' (/p/<id>/lists/<listId>).
  const [activeListId, setActiveListId] = useState(null);
  const openClaimList = (listId) => {
    setActiveListId(listId);
    setActivePage('lists');
  };
  const [user, setUser] = useState(null);
  const [viewAsClient, setViewAsClient] = useState(readViewAsClient);
  const [checkingAuth, setCheckingAuth] = useState(true);
  // Set to 'no_local_account' when Clerk has a valid session but the local
  // backend has no matching User row (e.g. someone signed into Clerk but was
  // never invited to Atlas). Surfaces an error screen with a Sign-out button
  // instead of an infinite spinner.
  const [authError, setAuthError] = useState(null);
  const [practices, setPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [resetToken, setResetToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_token') || null;
  });
  const [inviteToken, setInviteToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('invite_token') || null;
  });
  const [tokenUid, setTokenUid] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('uid') || null;
  });
  const selectedPracticeRef = useRef(null);

  // URL-sync bookkeeping. `restored` flips true after the initial URL->state
  // restore so the write effect and auto-select don't run prematurely.
  // `firstWrite` makes the first URL alignment a replaceState (no junk history
  // entry); every navigation after that pushes so Back/Forward walk the app.
  const [restored, setRestored] = useState(false);
  const firstWrite = useRef(true);

  // Keep ref in sync with state (avoids stale closures in loadPractices)
  useEffect(() => {
    selectedPracticeRef.current = selectedPractice;
  }, [selectedPractice]);

  // Derived role helpers. Everything below reads the EFFECTIVE role: a super_admin
  // with "View as client" on is a client for every prop a page takes (readOnly,
  // isAdmin, isSuperAdmin), so the preview is exactly what a client sees. Only the
  // sidebar's role badge shows the real role.
  // The preview needs an open practice: with none selected the flag is treated as off
  // (and left as it is in sessionStorage), so a super_admin is never stranded on the
  // Practices page with a client nav and no way back.
  const previewing = Boolean(viewAsClient && selectedPractice && user && user.role === 'super_admin');
  const effectiveRole = user ? (previewing ? 'client' : user.role) : null;
  // A page a client cannot see (deep link, Back button) lands on the dashboard, so the
  // nav highlights Dashboard and the URL mirror writes /dashboard rather than /reports.
  const clientLanding = (page) =>
    page && (user.role === 'client' || (viewAsClient && user.role === 'super_admin')) && !CLIENT_PAGES.includes(page)
      ? 'dashboard'
      : page;
  const clientView = effectiveRole === 'client';
  const isAdmin = user && (effectiveRole === 'admin' || effectiveRole === 'super_admin');
  const isSuperAdmin = user && effectiveRole === 'super_admin';
  const isReadOnly = clientView;
  // The onboarding pages exist for this user only when the deployment's flag is on AND
  // they are an admin; /api/users/me already folds both into one boolean.
  const canOnboard = Boolean(isAdmin && user && user.onboarding_enabled);

  // The capability gate for the open practice. Fetched once per practice and cached
  // in the hook's module-level store, so every page and the sidebar read one answer.
  // While it loads, every feature reads false — a gated page waits rather than
  // flashing a surface this practice may not have.
  const {
    features,
    appointments: appointmentsSignal,
    loading: capabilitiesLoading,
    error: capabilitiesError,
  } = useCapabilities(selectedPractice ? selectedPractice.id : null);
  // The Appointments tab shows for any practice whose appointments table has rows
  // (Olympus's sheet visits, Cobalt's schedule, a Kinnser visits export) or whose
  // sync could fill it. Unknown while the fetch is in flight, so hidden, like the
  // feature-gated pages.
  const showAppointments = Boolean(
    appointmentsSignal && (appointmentsSignal.has_rows || appointmentsSignal.sync_available)
  );

  // Auth: Clerk-only. Legacy cookie session path was removed along with the
  // /api/auth/* routes.
  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    if (!clerkLoaded) return; // wait for Clerk to initialise

    const fetchMe = async () => {
      const response = await fetch(`${API_URL}/api/users/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) return { ok: true, user: data.user };
      }
      return { ok: false };
    };

    const run = async () => {
      setAuthError(null);
      if (!clerkSignedIn) {
        setCheckingAuth(false);
        return;
      }
      try {
        // First attempt.
        let result = await fetchMe();
        // Retry once after a short delay as a safety net — the backend's
        // auto-claim fallback (now reading email from the JWT claim rather
        // than via a Clerk API round-trip) normally resolves the webhook
        // race inline, but keep this for cold-start edge cases.
        if (!result.ok) {
          await new Promise(r => setTimeout(r, 1500));
          result = await fetchMe();
        }
        if (result.ok) {
          setUser(result.user);
        } else {
          setAuthError('no_local_account');
        }
      } catch (err) {
        setAuthError('no_local_account');
      } finally {
        setCheckingAuth(false);
      }
    };

    run();
  }, [clerkLoaded, clerkSignedIn]);

  // Load practices when user is authenticated
  const loadPractices = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/practices`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to load practices: ${response.status}`);
    }
    const data = await response.json();
    setPractices(data.practices || []);
    // If a practice is selected, refresh its full details
    const current = selectedPracticeRef.current;
    if (current) {
      try {
        const fullResp = await fetch(`${API_URL}/api/practices/${current.id}`, {
          credentials: 'include',
        });
        if (fullResp.ok) {
          const fullData = await fullResp.json();
          setSelectedPractice(fullData);
        }
      } catch (err) {
        // Non-critical — practice list already loaded
      }
    }
  }, []);

  const [practicesLoaded, setPracticesLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      setPracticesLoaded(false);
      // Retry up to 2 times with a short delay (handles session not yet established)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await loadPractices();
          if (!cancelled) setPracticesLoaded(true);
          return;
        } catch (err) {
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
          }
        }
      }
      // All retries failed — still mark loaded so user sees empty state, not infinite spinner
      if (!cancelled) setPracticesLoaded(true);
    };

    load();
    return () => { cancelled = true; };
  }, [user, loadPractices]);

  // Auto-select the user's single practice — no reason to show a one-option
  // picker. Previously this skipped super_admin, but that just meant a flash
  // of the practices page on reload for single-practice admins. Admins can
  // still click "All Practices" in the sidebar to get back to the picker.
  useEffect(() => {
    if (restored && practicesLoaded && !selectedPractice && practices.length === 1) {
      handleSelectPractice(practices[0]);
    }
  }, [restored, practicesLoaded, practices, selectedPractice]); // eslint-disable-line

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    // Clerk-only. signOut revokes the current JWT and clears Clerk's session
    // cookie; after that, <ClerkProvider>'s afterSignOutUrl sends the user
    // back to the sign-in screen.
    try {
      await clerk.signOut();
    } catch (err) {
      // Ignore signOut errors — proceed to clear local state regardless.
    }
    setUser(null);
    setSelectedPractice(null);
    setViewAsClient(false);
    writeViewAsClient(false);
  };

  // The "View as client" switch. Turning it on from a page a client cannot see lands
  // on the dashboard, the client's first page, rather than leaving a staff page open
  // under a client nav.
  const handleToggleViewAsClient = () => {
    const next = !viewAsClient;
    setViewAsClient(next);
    writeViewAsClient(next);
    if (next && !CLIENT_PAGES.includes(activePage)) setActivePage('dashboard');
  };

  const fetchFullPractice = async (practiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      // Fall through
    }
    return null;
  };

  const handleSelectPractice = async (practice) => {
    const full = await fetchFullPractice(practice.id);
    setSelectedPractice(full || practice);
    setActivePage('dashboard');
  };

  const handlePracticeChange = async (practice) => {
    const full = await fetchFullPractice(practice.id);
    const next = full || practice;
    setSelectedPractice(next);
    // A claim list belongs to one practice: switching away from it lands on the Claims
    // page (and its Lists strip) instead of /p/<new>/lists/<old>, a list that is not there.
    if (activePage === 'lists') setActivePage('claims');
  };

  // From a cell on the Data readiness grid to that practice's Setup > Data page ('data' is
  // the feature page id in features/registry.js). Selects the practice first, the same way
  // the picker does, so the Data page mounts with the full practice.
  const handleOpenPracticeData = async (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;
    await handlePracticeChange(practice);
    setActivePage('data');
  };

  const handleBackToPractices = () => {
    setSelectedPractice(null);
    // If the user was on the admin page (selectedPractice already null), also
    // reset activePage so the PracticesPage actually renders. Without this,
    // clicking "All Practices" from the admin page leaves activePage='admin'
    // and the admin branch keeps winning the render.
    setActivePage('dashboard');
  };

  const handleResetComplete = () => {
    setResetToken(null);
    setInviteToken(null);
    setTokenUid(null);
    window.history.replaceState({}, '', window.location.pathname);
  };

  // --- URL <-> navigation-state sync ---

  // Restore navigation state FROM the URL once practices have loaded (deep
  // links, hard refresh). Runs once; takes precedence over the single-practice
  // auto-select above. A practice path carries only the id, so we fetch the
  // full practice via the existing fetchFullPractice helper.
  useEffect(() => {
    if (!user || !practicesLoaded || restored) return;
    const { practiceId, page, claimId, listId } = parsePath(window.location.pathname);
    // A /claims/<id> deep link names the claim for the ledger, which opens the editor
    // drawer over itself. One module-scoped value, as the editor page already used.
    if (claimId) setClaimEditorTarget(claimId, practiceId);
    if (listId) setActiveListId(listId);
    if (practiceId != null) {
      const match = practices.find((p) => p.id === practiceId);
      if (match) {
        (async () => {
          const full = await fetchFullPractice(practiceId);
          setSelectedPractice(full || match);
          if (page) setActivePage(clientLanding(page));
          firstWrite.current = false; // URL already matches; later navs push
          setRestored(true);
        })();
        return;
      }
    }
    // No practice in the URL (or no access to it): keep default behavior.
    if (page === 'admin' && isAdmin) setActivePage('admin');
    if (page === 'onboarding' && canOnboard) setActivePage('onboarding');
    setRestored(true);
  }, [user, practicesLoaded, practices, restored, isAdmin, canOnboard]); // eslint-disable-line

  // Mirror navigation state -> URL. First alignment replaces (no junk entry);
  // subsequent navigations push so Back/Forward walk the app.
  useEffect(() => {
    if (!user || !restored || resetToken || inviteToken) return;
    const target = stateToPath(selectedPractice, activePage, activeListId);
    if (target !== window.location.pathname) {
      if (firstWrite.current) {
        window.history.replaceState({}, '', target);
      } else {
        window.history.pushState({}, '', target);
      }
      firstWrite.current = false;
    }
  }, [user, restored, selectedPractice, activePage, activeListId, resetToken, inviteToken]);

  // Back/forward: reconcile state from the URL. The browser has already updated
  // location, so the write effect computes an equal path and won't re-push.
  useEffect(() => {
    if (!user) return;
    const onPop = () => {
      const { practiceId, page, claimId, listId } = parsePath(window.location.pathname);
      if (claimId) setClaimEditorTarget(claimId, practiceId);
      if (listId) setActiveListId(listId);
      if (practiceId != null) {
        const match = practices.find((p) => p.id === practiceId);
        setSelectedPractice((current) =>
          current && current.id === practiceId ? current : (match || current)
        );
        if (match) {
          fetchFullPractice(practiceId).then((full) => {
            if (full) setSelectedPractice(full);
          });
        }
        if (page) setActivePage(clientLanding(page));
      } else {
        setSelectedPractice(null);
        setActivePage(page === 'admin' ? 'admin' : page === 'onboarding' && canOnboard ? 'onboarding' : 'dashboard');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user, practices, viewAsClient]); // eslint-disable-line

  // Idle auto-logout, mounted in every authenticated view below. The warning
  // renders via a portal, so dropping it anywhere is fine; it's enabled only
  // once the user is authenticated.
  const idleGuard = (
    <IdleTimeout
      enabled={!!user}
      timeoutMs={idleTimeoutMinutes * 60 * 1000}
      warningMs={idleWarningSeconds * 1000}
      onTimeout={handleLogout}
    />
  );

  // Show loading while Clerk initializes or while checking auth.
  if (!clerkLoaded || checkingAuth) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Clerk session exists but backend has no matching local user — show a
  // clear error with a Sign-out button so the user can escape the state.
  if (!user && authError === 'no_local_account') {
    return (
      <div className="login-container">
        <div className="login-box auth-error">
          <p className="auth-error-message">
            The account you signed in with doesn't have access to Atlas.
            An administrator needs to invite you before you can sign in.
          </p>
          <button className="login-button" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated by either path → show Clerk's hosted sign-in component.
  // Self-signup is disabled in the Clerk dashboard (Restrictions → invitation
  // only), so this surface only allows signing in or completing an invite link.
  if (!user) {
    const origin = window.location.origin;
    const onSignUpRoute = isSignUpRoute();
    if (!onSignUpRoute && carriesClerkTicket()) {
      // An invitation link that landed on "/" (sent before this fix, or typed):
      // hand it to the sign-up route with its ticket, same host.
      window.location.replace(`${origin}${SIGN_UP_PATH}${window.location.search}${window.location.hash}`);
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      );
    }
    return (
      <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <SignedOut>
          {onSignUpRoute ? (
            <SignUp
              routing="hash"
              // Consumes the invitation's __clerk_ticket (strategy "ticket") and
              // lands the new user on this host's root, forced, like <SignIn>.
              forceRedirectUrl={`${origin}/`}
              signInForceRedirectUrl={`${origin}/`}
              signInUrl={`${origin}/`}
              appearance={{ elements: { footerAction: { display: 'none' } } }}
            />
          ) : (
          <SignIn
            routing="hash"
            // Absolute on this origin, force not fallback (see index.js): the user
            // who signed in on next stays on next whatever the Clerk dashboard says.
            // The path is the one they opened, so a deep link survives the sign-in.
            forceRedirectUrl={`${origin}${postSignInPath(window.location.pathname)}`}
            signUpForceRedirectUrl={`${origin}/`}
            signUpUrl={`${origin}${SIGN_UP_PATH}`}
            appearance={{
              elements: {
                // Atlas is invite-only. Hide Clerk's "Don't have an account?
                // Sign up" footer link — even though Clerk's Restrictions
                // mode should also be set to invitation-only in the dashboard
                // (belt-and-suspenders).
                footerAction: { display: 'none' },
              },
            }}
          />
          )}
        </SignedOut>
        <SignedIn>
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        </SignedIn>
      </div>
    );
  }

  // Show loading while practices are being fetched, OR while the single-
  // practice auto-select is in flight (prevents a brief flash of the
  // practices picker before the app shell renders).
  const autoSelectPending =
    practicesLoaded && !selectedPractice && practices.length === 1;
  if (!practicesLoaded || autoSelectPending) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show practices landing page if no practice selected
  if (!selectedPractice) {
    // Admin is accessible without a practice selected (for any admin), and so is Mission
    // Control — it spans every practice the user can see and deliberately takes no
    // selectedPractice, so forcing a drill-into-one-practice first made the page unreachable
    // from the Practices landing page.
    if (((activePage === 'admin' || activePage === 'rules') && isAdmin) || ((activePage === 'mission-control' || activePage === 'exec-review' || activePage === 'data-readiness') && isSuperAdmin) || (activePage === 'onboarding' && canOnboard)) {
      return (
        <div className="app">
          <Sidebar
            activePage={activePage === 'lists' ? 'claims' : activePage}
            onPageChange={setActivePage}
            user={user}
            onLogout={handleLogout}
            practices={practices}
            selectedPractice={null}
            onPracticeChange={handlePracticeChange}
            onBackToPractices={handleBackToPractices}
            viewAsClient={viewAsClient}
            onToggleViewAsClient={handleToggleViewAsClient}
          />
          <main className="main-content">
            {activePage === 'mission-control'
              ? <MissionControlPage />
              : activePage === 'exec-review'
              ? <ExecReviewPage />
              : activePage === 'data-readiness'
              ? <DataReadinessPage onOpenPracticeData={handleOpenPracticeData} />
              : activePage === 'rules'
              ? <RulesPage practices={practices} isSuperAdmin={isSuperAdmin} />
              : activePage === 'onboarding'
              ? <OnboardingPage isSuperAdmin={isSuperAdmin} />
              : <AdminPage practices={practices} onPracticesChanged={loadPractices} isSuperAdmin={isSuperAdmin} onOpenPracticeData={handleOpenPracticeData} canOnboard={canOnboard} onOpenPractice={handleSelectPractice} />}
          </main>
          {idleGuard}
        </div>
      );
    }
    return (
      <>
        {idleGuard}
        <PracticesPage
          practices={practices}
          onSelectPractice={handleSelectPractice}
          onPracticesUpdated={loadPractices}
          onGoToAdmin={() => setActivePage('admin')}
          userRole={effectiveRole}
          canOnboard={canOnboard}
        />
      </>
    );
  }

  const renderPage = () => {
    // The client view has a fixed page set (Sidebar's CLIENT_PAGES). A deep link or a
    // stale activePage to anything else lands on the dashboard rather than rendering a
    // staff page under a client nav; the backend refuses the data either way.
    if (clientView && !CLIENT_PAGES.includes(activePage)) {
      return <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
    }
    // Registry pages (the full-PMS sprint's gated surfaces) resolve before the
    // switch: one lookup instead of a case per feature, and one place where "the
    // feature is off" turns into the FeatureOff panel rather than an empty page.
    const featurePage = findFeaturePage(activePage);
    if (featurePage) {
      const { Page, feature, adminOnly } = featurePage;
      if (adminOnly && !isAdmin) {
        return <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      }
      if (capabilitiesLoading) {
        return (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        );
      }
      // A failed fetch is not an answer. FeatureOff would say "nothing connected to
      // this practice supplies it", which is a confident claim about their data feed
      // invented by a network error.
      if (capabilitiesError) {
        return (
          <div className="feature-off">
            <div className="feature-off-card">
              <h2>Couldn't load this practice's features</h2>
              <p>{capabilitiesError} Reload the page to try again.</p>
            </div>
          </div>
        );
      }
      if (!features[feature]) {
        return (
          <FeatureOff
            feature={feature}
            practiceName={selectedPractice.name}
            isAdmin={isAdmin}
            onOpenPracticeDetails={() => setActivePage('practice-details')}
          />
        );
      }
      return (
        <Page
          selectedPractice={selectedPractice}
          features={features}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          readOnly={isReadOnly}
          onPageChange={setActivePage}
          currentUserId={user?.id ?? null}
        />
      );
    }

    switch (activePage) {
      case 'dashboard':
        return <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      case 'eligibility':
        return <EligibilityPage selectedPractice={selectedPractice} readOnly={isReadOnly} onPracticeUpdated={loadPractices} />;
      case 'practice-details':
        return <PracticeDetailsPage selectedPractice={selectedPractice} onPracticeUpdated={loadPractices} readOnly={isReadOnly} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} canOnboard={canOnboard} />;
      case 'claims':
        return <ClaimsLedgerPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} onPageChange={setActivePage} onOpenList={openClaimList} />;
      // One claim list (/p/<id>/lists/<listId>): its top bar over the ledger narrowed to it.
      case 'lists':
        return activeListId ? (
          <ClaimListPage
            key={`${selectedPractice.id}-${activeListId}`}
            selectedPractice={selectedPractice}
            listId={activeListId}
            readOnly={isReadOnly}
            isAdmin={isAdmin}
            onPageChange={setActivePage}
            onOpenList={openClaimList}
            onBack={() => setActivePage('claims')}
          />
        ) : (
          <ClaimsLedgerPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} onPageChange={setActivePage} onOpenList={openClaimList} />
        );
      case 'payments':
        return <PaymentsPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} />;
      case 'ar':
        return <ARPage selectedPractice={selectedPractice} readOnly={isReadOnly} onOpenList={openClaimList} />;
      // David's audit view: the gap to 98% as blocks with the claims behind each.
      case 'waterfall':
        return <WaterfallPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onSelectPractice={handlePracticeChange} onOpenList={openClaimList} />;
      // The weekly report's numbers for the open practice, as the Slack tab shows them.
      // Staff only: a client's view lands on the dashboard (CLIENT_PAGES keeps it off).
      case 'success':
        return clientView
          ? <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />
          : <SuccessPage selectedPractice={selectedPractice} />;
      // ALWAYS the cross-practice grid, even with a practice open. It sits beside Admin as a
      // supervision page rather than a page about the selected practice, so scoping it to
      // whichever practice happened to be selected would make the same menu item mean two
      // different things. Deliberately passes no selectedPractice, which is what makes the
      // page call /api/mission-control rather than the per-practice endpoint.
      case 'mission-control':
        return isSuperAdmin ? <MissionControlPage /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // The weekly ledger review for every practice, as David reads it on Monday. Cross-practice
      // like Mission Control, so it deliberately takes no selectedPractice.
      case 'exec-review':
        return isSuperAdmin ? <ExecReviewPage /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // Which data streams reach Atlas for every practice. Cross-practice like Mission
      // Control, so it deliberately takes no selectedPractice; a cell click selects one.
      case 'data-readiness':
        return isSuperAdmin ? <DataReadinessPage onOpenPracticeData={handleOpenPracticeData} /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // Cross-practice like Mission Control: rules attach to payers/plans, not the
      // open practice, so it deliberately takes no selectedPractice.
      case 'rules':
        return isAdmin ? <RulesPage practices={practices} isSuperAdmin={isSuperAdmin} /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // Cross-practice like Mission Control; the flag and role are folded into canOnboard.
      case 'onboarding':
        return canOnboard ? <OnboardingPage isSuperAdmin={isSuperAdmin} /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // Staff: admin-only, as before. Client view: always, read-only — the page shows
      // its empty state when the practice has no appointment rows yet.
      case 'appointments':
        return isAdmin || clientView
          ? <AppointmentsPage selectedPractice={selectedPractice} readOnly={isReadOnly} />
          : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // The practice's client tasks, the same rows as the Slack app's Tasks tab. Staff
      // only: the client view never reaches this case (CLIENT_PAGES guard above).
      case 'tasks':
        return <TasksPage selectedPractice={selectedPractice} />;
      // The practice's mail threads, the same rows as the Slack app's Mail tab. Staff
      // only: a client's view lands on the dashboard (CLIENT_PAGES keeps it off).
      case 'mail':
        return clientView
          ? <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />
          : <MailPage selectedPractice={selectedPractice} />;
      case 'admin':
        return isSuperAdmin ? <AdminPage practices={practices} onPracticesChanged={loadPractices} isSuperAdmin={isSuperAdmin} onOpenPracticeData={handleOpenPracticeData} /> : <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      default:
        return <DashboardPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
    }
  };

  return (
    <div className="app">
      <Sidebar
        activePage={activePage === 'lists' ? 'claims' : activePage}
        onPageChange={setActivePage}
        user={user}
        onLogout={handleLogout}
        practices={practices}
        selectedPractice={selectedPractice}
        onPracticeChange={handlePracticeChange}
        onPracticesUpdated={loadPractices}
        onBackToPractices={handleBackToPractices}
        features={features}
        showAppointments={showAppointments}
        viewAsClient={viewAsClient}
        onToggleViewAsClient={handleToggleViewAsClient}
      />
      <main className="main-content">
        {renderPage()}
      </main>
      {idleGuard}
    </div>
  );
}

export default App;
